import { Link, useSearchParams } from "react-router-dom";
import qs from "qs";
import swal from "sweetalert2";
import React, { useCallback, useEffect, useState } from "react";
import Select from "react-dropdown";
import { URL } from "../../../constants/URL";
import CS from "../../../services/commonService";
import BreadCrumbs from "../../common/breadcrumbs";
import Modal from "../../common/Modal";
import SearchBox from "../../common/searchBox";
import Details from "./details";
import Icon from "../../icons/index";
import ReactTooltip from "react-tooltip";
import { getCurrentTimezoneDate } from "../../../util/columnsOrdering";
import Table from "../../common/table";
import {
	useGetHelpQueries,
	useDeleteHelpQuery,
	useUpdateHelpQuery,
	useGetHelpTopics,
} from "../../../queryHooks";
import { ROLES_CONFIG } from "../../../config/configs";
import { useStore } from "../../../zustand/store";
import { pageToLoadOnDelete } from "../../../helpers";
//	Pre-defined values
const options = [
	{ value: "OPEN", label: "Open" },
	{ value: "RESOLVED", label: "Close" },
];
const columnKey = "helpQueriesUsers";
//	Component starts
const HelpQueriesUsers = () => {
	const [params] = useSearchParams();
	//	State values
	const setLoading = useStore((state) => state.setLoading);
	const [query, setQuery] = useState({});
	const [open, setOpen] = useState(false);
	const [helpTopics, setHelpTopics] = useState([]);
	const [users, setUsers] = useState([]);
	const [tableData, setTableData] = useState();
	const [pageNow, setPageNow] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [helpQueriesPayload, setHelpQueriesPayload] = useState({
		pagination: true,
		items_per_page: itemsPerPage,
		page: pageNow,
		is_restaurant: 0,
	});

	const {
		data: dataGetHelpQueries,
		isLoading,
		refetch,
	} = useGetHelpQueries(qs.stringify(helpQueriesPayload));

	useEffect(() => {
		if (dataGetHelpQueries && dataGetHelpQueries.status === "success") {
			let respData = dataGetHelpQueries.data;
			respData.page = pageNow;
			respData.items_per_page = itemsPerPage;
			setTableData(respData);
		}
	}, [dataGetHelpQueries]);

	const { data: dataGetHelpTopic } = useGetHelpTopics(
		qs.stringify({
			pagination: false,
			filter: { ["help_topics.role_id"]: ROLES_CONFIG.USER },
		})
	);

	useEffect(() => {
		if (dataGetHelpTopic && dataGetHelpTopic.data?.records) {
			let _helpTopics = [];
			dataGetHelpTopic.data.records.forEach((element) => {
				_helpTopics.push({
					label: element.name,
					value: element.id,
				});
			});
			setHelpTopics(_helpTopics);
		}
	}, [dataGetHelpTopic]);

	const {
		mutateAsync: updateHelpQuery,
		error: errorUpdateHelpQuery,
		data: dataUpdateHelpQuery,
	} = useUpdateHelpQuery();

	useEffect(() => {
		if (dataUpdateHelpQuery) {
			setLoading(false);
			if (dataUpdateHelpQuery.status === "success") {
				let _page = pageToLoadOnDelete(
					tableData?.total_items,
					1,
					pageNow,
					itemsPerPage
				);
				if (_page === pageNow) refetch();
				setHelpQueriesPayload({ ...helpQueriesPayload, page: _page });
				setPageNow(_page);
				CS.showSuccessMessage("Status updated successfully");
			} else if (dataUpdateHelpQuery.status === "fail") {
				CS.showErrorMessage(dataUpdateHelpQuery.message);
			}
		}
	}, [dataUpdateHelpQuery]);

	useEffect(() => {
		if (errorUpdateHelpQuery) {
			setLoading(false);
			CS.showErrorMessage(errorUpdateHelpQuery.response?.data?.message);
		}
	}, [errorUpdateHelpQuery]);

	const {
		mutateAsync: deleteHelpQuery,
		error: errorDeleteHelpQuery,
		data: dataDeleteHelpQuery,
	} = useDeleteHelpQuery();

	useEffect(() => {
		if (dataDeleteHelpQuery) {
			setLoading(false);
			if (dataDeleteHelpQuery.status === "success") {
				CS.showSuccessMessage(dataDeleteHelpQuery.message);
				refetch();
			} else if (dataDeleteHelpQuery.status === "fail") {
				CS.showErrorMessage(dataDeleteHelpQuery.message);
			}
		}
	}, [dataDeleteHelpQuery]);

	useEffect(() => {
		if (errorDeleteHelpQuery) {
			setLoading(false);
			CS.showErrorMessage(errorDeleteHelpQuery.response?.data?.message);
		}
	}, [errorDeleteHelpQuery]);

	/******************************************************************************************
	@Purpose    :   To close the modal
	*******************************************************************************************/
	const closeModal = useCallback(() => setOpen(false), []);

	const columns = [
		{
			label: "Query Id",
			type: "string",
			path: "id",
			alias: "id",
		},
		{
			label: "Email",
			type: "string",
			path: "email",
			sort: true,
			alias: "users.email",
			aliasFilter: "users.email",
			filterUsers: true,
			content: (item) => (
				<Link
					to={{
						pathname: `${URL.onboarderedUsers}/${item.user_id}`,
						state: { key: "helpQueries" },
					}}
				>
					<div className="d-flex align-items-center">
						{/* <div className="flex-auto">{CS.renderImageUrl(item.photo)}</div> */}
						<div className="d-flex flex-column flex-wrap">
							<span className="pl-2">{item.email}</span>
						</div>
					</div>
				</Link>
			),
		},
		{
			label: "Topic",
			type: "string",
			sort: true,
			path: "topic_name",
			alias: "help_topics.name",
			aliasFilter: "help_topics.id",
			filterOptions: helpTopics,
		},
		{
			label: "Created On",
			type: "date",
			sort: true,
			path: "created_at",
			alias: "help_queries.created_at",
			filterDate: true,
			content: (item) => getCurrentTimezoneDate(item.created_at),
		},
		{
			label: "Status",
			type: "boolean",
			path: "status",
			aliasFilter: "help_queries.status",
			filterOptions: options,
			content: (item) =>
				item.status === "OPEN" ? (
					<Select
						className="badge badge-pending drop-up"
						value={options.find((option) => option.value === item.status)}
						onChange={(option) => {
							updateHelpQuery({ id: item.id, status: option.value });
							setLoading(true);
						}}
						options={options}
					/>
				) : (
					<p className="badge badge-light-danger mb-0">Closed</p>
				),
		},
		{
			label: "Closed Date",
			type: "string",
			sort: true,
			path: "resolved_at",
			alias: "help_queries.resolved_at",
			filterDate: true,
			content: (item) =>
				item.status !== "OPEN"
					? item.resolved_at && getCurrentTimezoneDate(item.resolved_at)
					: "--",
		},
		{
			label: "Action",
			type: "boolean",
			content: (item) => (
				<>
					<Icon
						type="show"
						data-tip
						data-for="show"
						onClick={() => {
							setOpen(true);
							setQuery(item);
						}}
					/>
					<ReactTooltip id="show" place="top" effect="solid">
						Show
					</ReactTooltip>
					<Icon
						type="delete"
						data-tip
						data-for="delete"
						onClick={() =>
							swal
								.fire({
									title: "Are you sure you want to delete?",

									icon: "warning",
									showCancelButton: true,
									confirmButtonColor: "#3085d6",
									cancelButtonColor: "#d33",
									confirmButtonText: "Yes, delete it!",
								})
								.then((result) => {
									if (result.isConfirmed) {
										{
											deleteHelpQuery(item.id);
											setLoading(true);
										}
									}
								})
						}
					/>
					<ReactTooltip id="delete" place="top" effect="solid">
						Delete
					</ReactTooltip>
				</>
			),
		},
	];

	return (
		<>
			<BreadCrumbs
				title="Help Management"
				data={[{ label: "Help" }, { label: "Help Queries Users" }]}
			/>
			<div className="dataTables_wrapper">
				<div className="content-area position-relative">
					<div className="grid">
						<div className="grid-content">
							<div className="user-listing-filterOptions mb-2 mt-3 d-block">
								<div className="row mb-2">
									<div className="col-sm-8 mb-2 position-static">
										<div className="left-buttons d-flex">
											<SearchBox
												columnKey={columnKey}
												searchValue={params.get("id") ? params.get("id") : ""}
												search={(value) => {
													setPageNow(1);
													setHelpQueriesPayload({
														...helpQueriesPayload,
														search: {
															"help_topics.name":
																!value && value === "" ? undefined : value,
															"users.email":
																!value && value === "" ? undefined : value,
														},
														search_filter: {
															"help_queries.id":
																!value && value === "" ? undefined : value,
														},
														page: 1,
													});
												}}
											/>
										</div>
									</div>
									<div className="col-sm-4">
										<div className="right-buttons d-flex justify-content-end"></div>
									</div>
								</div>
							</div>
							<Modal open={open} onCloseModal={closeModal}>
								<Details query={query} onCloseModal={closeModal} />
							</Modal>
							<Table
								columnKey={columnKey}
								columns={columns}
								tableData={tableData}
								sortCol={(obj) => {
									setPageNow(1);
									setHelpQueriesPayload({
										...helpQueriesPayload,
										sort: {
											[obj.alias]:
												!obj.sort && obj.sort === "" ? undefined : obj.sort,
										},
										page: 1,
									});
								}}
								setCurrentPage={(page) => {
									setPageNow(page);
									setHelpQueriesPayload({
										...helpQueriesPayload,
										page,
									});
								}}
								setCurrentLimit={(items_per_page) => {
									setPageNow(1);
									setItemsPerPage(items_per_page);
									setHelpQueriesPayload({
										...helpQueriesPayload,
										items_per_page,
										page: 1,
									});
								}}
								loading={isLoading}
								filterValue={async (obj) => {
									if (obj.filter === null) {
										const resp = await CS.filterData(
											helpQueriesPayload.filter,
											obj
										);
										setPageNow(1);
										setHelpQueriesPayload({
											...helpQueriesPayload,
											filter: resp,
											page: 1,
										});
									} else {
										setPageNow(1);
										setHelpQueriesPayload({
											...helpQueriesPayload,
											filter: {
												...helpQueriesPayload.filter,
												[obj.alias]: obj.filter,
											},
											page: 1,
										});
									}
								}}
								filterDate={(dates) => {
									setPageNow(1);
									setHelpQueriesPayload({
										...helpQueriesPayload,
										filter_date: dates && {
											...helpQueriesPayload?.filter_date,
											[dates.alias]: String(dates.filter),
										},
										page: 1,
									});
								}}
							/>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default HelpQueriesUsers;
